/**
 * /compliance — Compliance alert queue + fact review.
 *
 * Shows AI-extracted ComplianceFacts that scored below the
 * AUTO_APPLY_CONFIDENCE_THRESHOLD (0.95) and are awaiting human
 * review before they are applied to the vendor qualification state.
 *
 * Tabs: Pending | Approved | Rejected
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useConstructionPack } from "@/lib/useConstructionPack";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Chip, CircularProgress, Tab, Tabs, Tooltip } from "@mui/material";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import { api } from "@/lib/api";
import { complianceAlertQueueQueryOptions } from "@/lib/queries";
import type { ComplianceAlertQueueItem } from "@/lib/types";
import { HelpHint } from "@/components/HelpHint";

export const Route = createFileRoute("/_auth/compliance")({
  component: CompliancePage,
});

// ─── helpers ─────────────────────────────────────────────────────────────────

function fmtDate(s: string) {
  return new Date(s).toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function ConfidenceBar({ value }: { value: number }) {
  const pct = Math.round(value * 100);
  const color = pct >= 90 ? "bg-emerald-500" : pct >= 75 ? "bg-amber-400" : "bg-red-400";
  return (
    <div className="flex items-center gap-2">
      <div className="w-16 h-1.5 bg-slate-200 dark:bg-gray-700 rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-slate-500 dark:text-slate-400">{pct}%</span>
    </div>
  );
}

const FACT_TYPE_LABELS: Record<string, string> = {
  "party.cert.submitted": "Cert submitted",
  "party.cert.verified": "Cert verified",
  "party.cert.expiry_warning": "Cert expiry warning",
  "party.cert.expired": "Cert expired",
  "party.license.submitted": "License submitted",
  "party.license.verified": "License verified",
  "party.osha.emr_updated": "EMR updated",
  "party.osha.trir_updated": "TRIR updated",
  "party.osha.incident": "OSHA incident",
  "party.qualification.approved": "Qual approved",
  "party.qualification.disqualified": "Disqualified",
  "outbound.lien_waiver.requested": "Lien waiver requested",
  "outbound.lien_waiver.received": "Lien waiver received",
  "system.expiry_scan": "Expiry scan",
};

function factTypeLabel(ft: string) {
  return FACT_TYPE_LABELS[ft] ?? ft.split(".").slice(-1)[0].replace(/_/g, " ");
}

// ─── review row ───────────────────────────────────────────────────────────────

function AlertRow({ item, onReview }: {
  item: ComplianceAlertQueueItem;
  onReview: (id: string, action: "approve" | "reject", notes?: string) => void;
}) {
  const [notes, setNotes] = useState("");
  const [expanded, setExpanded] = useState(false);
  const isPending = item.status === "pending";

  return (
    <div className={`border-b border-slate-100 dark:border-gray-800 last:border-0 transition-colors ${isPending ? "hover:bg-slate-50 dark:hover:bg-gray-800/50" : "opacity-70"}`}>
      <div className="flex items-start gap-3 px-4 py-3">
        {/* fact type + subject */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Chip
              label={factTypeLabel(item.fact_type)}
              size="small"
              variant="outlined"
              sx={{ fontSize: 11, height: 20 }}
            />
            <span className="text-xs text-slate-500 dark:text-slate-400">
              {item.subject_kind}
              {" · "}
              <Link
                to="/vendors/$vendorId/qualification"
                params={{ vendorId: item.subject_id }}
                className="text-blue-600 dark:text-blue-400 hover:underline font-mono"
              >
                {item.subject_id.slice(0, 8)}
              </Link>
            </span>
          </div>
          <ConfidenceBar value={item.confidence} />
          {expanded && Object.keys(item.payload).length > 0 && (
            <pre className="mt-2 text-[10px] bg-slate-100 dark:bg-gray-800 text-slate-700 dark:text-slate-300 rounded p-2 overflow-auto max-h-32">
              {JSON.stringify(item.payload, null, 2)}
            </pre>
          )}
        </div>

        {/* date */}
        <span className="text-xs text-slate-400 dark:text-slate-500 whitespace-nowrap hidden sm:block">
          {fmtDate(item.created_at)}
        </span>

        {/* status chip or action buttons */}
        {!isPending ? (
          <Chip
            label={item.status}
            size="small"
            color={item.status === "approved" ? "success" : "error"}
            variant="filled"
            sx={{ fontSize: 11, height: 20 }}
          />
        ) : (
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <Tooltip title="Expand payload">
              <button
                type="button"
                onClick={() => setExpanded(v => !v)}
                className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
              >
                <InfoOutlinedIcon sx={{ fontSize: 16 }} />
              </button>
            </Tooltip>
            {expanded && (
              <input
                type="text"
                placeholder="Notes (optional)"
                value={notes}
                onChange={e => setNotes(e.target.value)}
                className="text-xs border border-slate-300 dark:border-gray-700 rounded px-2 py-1 w-32 dark:bg-gray-800 dark:text-slate-100"
              />
            )}
            <Tooltip title="Approve — apply this fact to projections">
              <button
                type="button"
                onClick={() => onReview(item.id, "approve", notes)}
                className="p-1 text-emerald-600 hover:text-emerald-800 dark:text-emerald-400 dark:hover:text-emerald-200"
              >
                <CheckCircleOutlineIcon sx={{ fontSize: 20 }} />
              </button>
            </Tooltip>
            <Tooltip title="Reject — discard this fact">
              <button
                type="button"
                onClick={() => onReview(item.id, "reject", notes)}
                className="p-1 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-200"
              >
                <CancelOutlinedIcon sx={{ fontSize: 20 }} />
              </button>
            </Tooltip>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── page ────────────────────────────────────────────────────────────────────

function CompliancePage() {
  const { enabled: constructionEnabled } = useConstructionPack();
  if (!constructionEnabled) return null;
  const [tab, setTab] = useState(0);
  const qc = useQueryClient();

  const statusMap = ["pending", "approved", "rejected"] as const;
  const status = statusMap[tab];

  const queueQ = useQuery(complianceAlertQueueQueryOptions(status));
  const items: ComplianceAlertQueueItem[] = queueQ.data ?? [];

  const reviewMut = useMutation({
    mutationFn: ({ id, action, notes }: { id: string; action: "approve" | "reject"; notes?: string }) =>
      api(`/api/compliance/alert-queue/${id}/review`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action, notes }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["compliance"] });
    },
  });

  const pendingCount = useQuery(complianceAlertQueueQueryOptions("pending")).data?.length ?? 0;

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] bg-slate-50 dark:bg-gray-950">
      {/* header */}
      <div className="bg-white border-b border-slate-200 dark:bg-gray-900 dark:border-gray-800 px-6 py-4">
        <div className="flex items-center gap-3">
          <ShieldOutlinedIcon sx={{ fontSize: 22, color: "#3b82f6" }} />
          <div>
            <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Compliance</h1>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              AI-extracted facts below the auto-apply threshold — review before they update vendor status.
            </p>
          </div>
          {queueQ.isLoading && <CircularProgress size={14} />}
          <div className="ml-auto">
            <HelpHint docPath="compliance/overview.md" label="Compliance review help" />
          </div>
        </div>

        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v as number)}
          sx={{ mt: 1, minHeight: 36 }}
          TabIndicatorProps={{ style: { height: 2 } }}
        >
          <Tab
            label={`Pending review${pendingCount ? ` (${pendingCount})` : ""}`}
            sx={{ fontSize: 12, minHeight: 36, textTransform: "none", py: 0 }}
          />
          <Tab label="Approved" sx={{ fontSize: 12, minHeight: 36, textTransform: "none", py: 0 }} />
          <Tab label="Rejected" sx={{ fontSize: 12, minHeight: 36, textTransform: "none", py: 0 }} />
        </Tabs>
      </div>

      {/* content */}
      <div className="flex-1 overflow-auto p-6">
        {/* summary cards */}
        {tab === 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            <SummaryCard label="Pending review" value={pendingCount} color="amber" />
            <SummaryCard label="Auto-apply threshold" value="95%" color="blue" />
          </div>
        )}

        {queueQ.isError && (
          <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg p-4 text-sm text-red-700 dark:text-red-300 mb-4">
            {(queueQ.error as Error).message}
          </div>
        )}

        {items.length === 0 && !queueQ.isLoading && (
          <div className="text-center py-16 text-slate-400 dark:text-slate-500">
            <ShieldOutlinedIcon sx={{ fontSize: 48, color: "#cbd5e1", display: "block", margin: "0 auto 8px" }} />
            <p className="text-sm">No {status} items in the queue.</p>
            {status === "pending" && (
              <p className="text-xs mt-1">
                Facts with confidence ≥ 95% are applied automatically.
                Lower-confidence facts appear here for review.
              </p>
            )}
          </div>
        )}

        {items.length > 0 && (
          <div className="bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-800 rounded-lg overflow-hidden shadow-sm">
            <div className="border-b border-slate-100 dark:border-gray-800 px-4 py-2 flex items-center gap-3 bg-slate-50 dark:bg-gray-950">
              <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide flex-1">Fact type · Subject</span>
              <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide hidden sm:block w-36">Created</span>
              <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide w-24 text-right">
                {status === "pending" ? "Actions" : "Status"}
              </span>
            </div>
            {items.map(item => (
              <AlertRow
                key={item.id}
                item={item}
                onReview={(id, action, notes) => reviewMut.mutate({ id, action, notes })}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryCard({ label, value, color }: { label: string; value: string | number; color: "amber" | "blue" | "green" | "red" }) {
  const bg = { amber: "bg-amber-50 dark:bg-amber-950", blue: "bg-blue-50 dark:bg-blue-950", green: "bg-emerald-50 dark:bg-emerald-950", red: "bg-red-50 dark:bg-red-950" }[color];
  const text = { amber: "text-amber-700 dark:text-amber-300", blue: "text-blue-700 dark:text-blue-300", green: "text-emerald-700 dark:text-emerald-300", red: "text-red-700 dark:text-red-300" }[color];
  return (
    <div className={`${bg} rounded-lg p-3 border border-slate-100 dark:border-gray-800`}>
      <p className="text-xs text-slate-500 dark:text-slate-400">{label}</p>
      <p className={`text-2xl font-bold mt-0.5 ${text}`}>{value}</p>
    </div>
  );
}
